import { z } from 'zod';
import { MoneySchema } from './common/money.js';
import { BalanceCollectionMethodSchema } from './deposits.js';
// Payment processor used for a booking. Stored per booking, not per operator —
// refunds always use the processor that took the original payment, even if the
// operator has since switched. This is non-negotiable.
// See CLAUDE.md → "Things that are non-negotiable".
//
// Exported as a const tuple so the Drizzle pgEnum in @nuberu/api and this
// Zod schema share a single source of truth.
export const PAYMENT_PROCESSORS = ['stripe', 'redsys'];
export const PaymentProcessorSchema = z.enum(PAYMENT_PROCESSORS);
// Which Redsys gateway a credential row targets. `test` hits the sis-t
// sandbox host; `live` hits the production host. Chosen per operator so a
// business can validate the integration before going live without a code change.
export const REDSYS_ENVIRONMENTS = ['test', 'live'];
export const RedsysEnvironmentSchema = z.enum(REDSYS_ENVIRONMENTS);
// Non-secret Redsys terminal configuration, stored on
// `operator_credentials.config` (jsonb) for a Redsys row. The secret merchant
// key is encrypted into `encrypted_secret_key`; these three fields are safe to
// store in plaintext and are echoed back to the dashboard so the operator can
// see which terminal is connected. Null/absent for a Stripe row.
//   - merchantCode (FUC): the acquirer-assigned merchant id (numeric string).
//   - terminal: the terminal number within that merchant (1–999).
//   - environment: which Redsys gateway to hit (see RedsysEnvironment).
// Validated with this schema at every read/write boundary — the jsonb column
// carries no compile-time guarantee, so the shape is never trusted raw.
export const RedsysCredentialConfigSchema = z.object({
    merchantCode: z.string().regex(/^\d{1,9}$/, 'FUC must be 1–9 digits'),
    terminal: z.string().regex(/^\d{1,3}$/, 'terminal must be 1–3 digits'),
    environment: RedsysEnvironmentSchema,
});
// Payment lifecycle. Stored as `bookings.payment_state` (column on bookings,
// not a separate `payments` table — see docs/context/01-architecture.md → Why
// decisions for the rationale).
// - initiated: Payment Intent (Stripe) or signed request (Redsys) created;
//              awaiting customer completion.
// - succeeded: Webhook (Stripe) or server-to-server notification (Redsys)
//              confirmed the payment. Booking transitions to confirmed only
//              on this state.
// - failed:    Webhook reported failure (declined, timeout, fraud check).
//              Booking stays `pending` so the customer can retry.
// - refunded:  Full or partial refund processed and confirmed.
//
// Exported as a const tuple so the Drizzle pgEnum and this Zod schema share
// a single source of truth — same pattern as PAYMENT_PROCESSORS above.
export const PAYMENT_STATES = ['initiated', 'succeeded', 'failed', 'refunded'];
export const PaymentStateSchema = z.enum(PAYMENT_STATES);
// How an in-person / operator-recorded payment was collected. Online bookings
// pay through a processor (Stripe/Redsys) and leave this null; a booking paid
// at reception (or logged by the operator) records one of these. Stored as
// `bookings.payment_method`. Codes are English for consistency with the rest of
// the schema; the operator-facing Spanish labels live at the UI edge —
//   card → "Datáfono / TPV" · cash → "Efectivo" · transfer → "Transferencia"
//   bizum → "Bizum" · other → "Otro"
// (`card`/`cash` keep their prior meaning, so no existing row is rewritten;
// migration 0099 only ADDs the three new values.)
// Const tuple so the Drizzle pgEnum + this Zod schema share one source.
export const PAYMENT_METHODS = ['card', 'cash', 'transfer', 'bizum', 'other'];
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS);
// Path params for `POST /api/v1/sessions/:sessionId/bookings/:bookingId/payment-intent`.
// Both IDs are in the URL — the widget knows them after booking creation
// (step 7a returns the booking id; the session id is known from the calendar).
export const PaymentIntentParamsSchema = z.object({
    sessionId: z.string().uuid(),
    bookingId: z.string().uuid(),
});
// The signed redirect form a Redsys payment completes with — the widget
// auto-POSTs these three fields to `url` (the Redsys hosted gateway).
export const RedsysRedirectFormSchema = z.object({
    url: z.string().url(),
    dsSignatureVersion: z.string().min(1),
    dsMerchantParameters: z.string().min(1),
    dsSignature: z.string().min(1),
});
// How the widget completes a payment, discriminated by processor. Stripe mounts
// Elements with `clientSecret` (+ the operator's public `publishableKey`);
// Redsys auto-POSTs `redirectForm` to its hosted gateway (the redirect flow).
// This is the single source of truth for the shape — the API's provider
// `PaymentPresentation` is this exact type, so the server returns it verbatim.
export const PaymentCompletionSchema = z.discriminatedUnion('processor', [
    z.object({
        processor: z.literal('stripe'),
        clientSecret: z.string().min(1),
        publishableKey: z.string().min(1),
    }),
    z.object({
        processor: z.literal('redsys'),
        redirectForm: RedsysRedirectFormSchema,
    }),
]);
// Response shape for the payment-intent route. `payment` carries whatever the
// widget needs to complete the charge for the chosen processor. The server is
// the authority on the amount — returning it here keeps the displayed price
// in lockstep with what gets charged, even if the widget's cached booking row
// has drifted.
export const PaymentIntentResponseSchema = z.object({
    paymentIntentId: z.string().min(1),
    payment: PaymentCompletionSchema,
    // What is charged NOW. For a deposit booking this is the señal, not the full
    // total — `balance` then carries the rest.
    total: MoneySchema,
    // Present only for a deposit/señal booking (deposit-data-model): the amount
    // still owed after the deposit, how it's collected, and — for automatic
    // collection — when the off-session charge fires. Absent for a full charge.
    balance: z
        .object({
        amount: MoneySchema,
        collectionMethod: BalanceCollectionMethodSchema,
        // ISO 8601; null for manual collection (no scheduled charge).
        dueAt: z.string().nullable(),
    })
        .optional(),
});
//# sourceMappingURL=payment.js.map